import { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PageLoader from '@/components/PageLoader';
import SilentBoundary from '@/components/SilentBoundary';
import { lazyWithRetry } from '@/lib/chunkLoad';
import { backgroundForPath } from '@/lib/pageTheme';
import IntroSplash from '@/components/IntroSplash';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import Navbar from './Navbar';
import Footer from './Footer';

// PERFORMANS: SiteLayout kendisi (router.tsx'te) EAGER import edilir — her
// sayfa ziyaretinde main.js'in parçasıdır. Eskiden AnnouncementsDock (~17 kB
// kaynak) ve QuizBubble (~20 kB, çok adımlı test UI'ı dahil) burada statik
// import ediliyordu; ikisi de kapalı-varsayılan, katlama-altı yüzen widget'lar
// olduğu için main bundle'a gereksiz katkı yapıyorlardı. lazy() ile main.js'e
// girmezler; her ikisi de veri gelmeden zaten `return null` ettiği için
// fallback={null} görsel fark yaratmaz.
const AnnouncementsDock = lazyWithRetry(
  () => import('@/components/AnnouncementsDock'),
);
const QuizBubble = lazyWithRetry(() => import('@/components/QuizBubble'));

// Sayfa geçişinde ve yenilemede en üste kaydır; adreste hash varsa (ör.
// /institutional#gecmisten-gelecege) o bölüme in.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  // Tarayıcının son scroll konumunu geri yüklemesini engelle (yenilemede tepeden aç).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }
    // Hedef bölüm lazy sayfa içinde; henüz mount olmamış olabilir → kısa süre
    // (yaklaşık 2 sn) her karede aranır, bulununca kaydırılıp bırakılır.
    const id = decodeURIComponent(hash.slice(1));
    let raf = 0;
    const start = performance.now();
    const seek = (now: number) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
      if (now - start < 2000) raf = requestAnimationFrame(seek);
    };
    raf = requestAnimationFrame(seek);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);
  return null;
};

// Hero'su navbar'ın ALTINA giren sayfalar — bu iki sayfa ilk bölümünü `-mt-20`
// ile yukarı çeker (Home.tsx ve Corporate.tsx içindeki tek `-mt-20` kullanımı).
// Yükleme yer tutucusu da aynı hizada başlamalı, yoksa içerik gelince ~80px
// yukarı sıçrıyor.
const EDGE_TO_EDGE_ROUTES = new Set(['/', '/institutional']);

const SiteLayout = () => {
  const { pathname } = useLocation();
  // Sayfa zeminini ROTADAN, chunk gelmeden uygula.
  //
  // usePageBackground çağrıları lazy sayfaların İÇİNDE olduğu için zemin ancak
  // chunk indikten sonra doğru rengi alıyordu: yükleme ekranı varsayılan
  // lacivertte kalıyor, sayfa gelince ör. siyaha atlıyordu ("arka planlar
  // sayfaların arka planı ile aynı değil"). useLayoutEffect boyamadan ÖNCE
  // çalışır → ilk kareden itibaren doğru renk.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const bg = backgroundForPath(pathname);
    root.style.setProperty('--page-bg', bg);
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (meta) meta.content = bg;
  }, [pathname]);
  const [showDeferredWidgets, setShowDeferredWidgets] = useState(false);
  // Yumuşak scroll (Lenis) yalnızca ana sayfada; diğer tüm sayfalarda atalet
  // "yavaş/inemiyorum" hissi verdiği için native scroll kullanılır.
  useSmoothScroll(pathname === '/');

  useEffect(() => {
    let alive = true;
    let idle = 0;

    const run = () => {
      if (alive) setShowDeferredWidgets(true);
    };
    const schedule = () => {
      const ric = (
        window as Window &
          typeof globalThis & {
            requestIdleCallback?: (
              cb: () => void,
              options?: { timeout: number },
            ) => number;
          }
      ).requestIdleCallback;
      idle = ric ? ric(run, { timeout: 2500 }) : window.setTimeout(run, 1400);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      alive = false;
      window.removeEventListener('load', schedule);
      window.clearTimeout(idle);
    };
  }, []);

  return (
    <div className="relative flex min-h-[100lvh] flex-col">
      {/* Sayfa zemini — SABİT katman: mobil tarayıcıda üst/alt çubukların
          ALTINA kadar uzanır, içerik kesintisiz görünür. Rengi aktif sayfa
          usePageBackground ile belirler. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-20"
        style={{ background: 'var(--page-bg)' }}
      />

      {/* AURORA KALDIRILDI — bilerek.
          Her rota kendi tam ekran zeminini çiziyor (HomeBackdrop, ContentBackdrop,
          RadarBackdrop, program *Backdrop'ları, FormBackdrop, NotFound...), bu
          yüzden aurora yüklendikten SONRA hiçbir sayfada görünmüyordu. Yalnızca
          YÜKLEME anında görünüp iki soruna yol açıyordu:
            1) Program dalının opak sarmalayıcısı (bg-[#...]) navbar'ın ALTINDAN
               başlayıp aurora'yı örtüyor, tam o hizada sert bir dikiş oluşuyordu
               (ölçüldü: y=80 #1f1c1e → y=84 #100e0f).
            2) Mavi tonu, açılacak sayfanın zeminiyle uyuşmuyordu.
          Zemin rengi zaten --page-bg ile rotadan geliyor (bkz. pageTheme.ts). */}

      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Suspense
          // Yer tutucu ekranı doldurur (bkz. PageLoader): sayfa (lazy) gelene
          // kadar footer katlamanın altında kalır, içerik gelince sıçrama olmaz.
          fallback={
            <PageLoader edgeToEdge={EDGE_TO_EDGE_ROUTES.has(pathname)} />
          }
        >
          {/* key=pathname → route değişiminde içerik yeniden mount olur ve
              animate-pagefade ile yumuşakça belirir.
              data-page-content: sayfa (lazy chunk) GERÇEKTEN render edildi
              işareti. IntroSplash router'ın dışında olduğu için perdeyi ne
              zaman kaldıracağını bu işaretten öğrenir.
              min-h: PageLoader ile AYNI yükseklik. İçerik ekrandan kısaysa
              yükleyici gidince footer ekrana zıplıyordu (mobil CLS ~0.11). */}
          <div
            key={pathname}
            className="animate-pagefade min-h-[calc(100lvh-5rem)]"
            data-page-content
          >
            <Outlet />
          </div>
        </Suspense>
      </main>
      <Footer />
      {/* Yüzen widget'lar sayfa için şart değil: yüklenemez ya da çökerlerse
          yalnızca kendileri kaybolur. Sınır yokken QuizBubble chunk'ının
          yüklenememesi TÜM ana sayfayı hata ekranına çeviriyordu. */}
      {showDeferredWidgets && (
        <>
          <SilentBoundary>
            <Suspense fallback={null}>
              <AnnouncementsDock />
            </Suspense>
          </SilentBoundary>
          <SilentBoundary>
            <Suspense fallback={null}>
              <QuizBubble />
            </Suspense>
          </SilentBoundary>
        </>
      )}
      {pathname === '/' && <IntroSplash key={pathname} waitForFrame />}
    </div>
  );
};

export default SiteLayout;
